import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import { AUTH_CHANGED_EVENT, loadAuthSession } from "../../../services/authStorage";
import {
  AdminCreateUserPayload,
  AdminUpdateUserPayload,
  UserDto,
  UserRole,
  UserStatus,
  createUserAsAdmin,
  deleteUserAsAdmin,
  getUsersForAdmin,
  resetUserPasswordAsAdmin,
  updateUserAsAdmin,
} from "../../../services/userApi";
import {
  ActionRow,
  BadgeRow,
  Button,
  ContentContainer,
  Eyebrow,
  Field,
  FieldLabel,
  FormGrid,
  HeroBanner,
  HeroText,
  HeroTitle,
  Input,
  Message,
  NoticeCard,
  NoticeText,
  NoticeTitle,
  PasswordRow,
  RoleBadge,
  Select,
  StatusBadge,
  UserCard,
  UserHeader,
  UserSubline,
  UserTitle,
} from "./styles";

type EditableUser = UserDto & {
  passwordDraft: string;
  message: string;
  tone?: "error" | "success";
};

type UserSortMode =
  | "name-asc"
  | "name-desc"
  | "created-desc"
  | "created-asc"
  | "role-asc"
  | "role-desc"
  | "status-asc"
  | "status-desc";

type CreateUserForm = AdminCreateUserPayload;

const ROLE_LABELS: Record<UserRole, string> = {
  mitglied: "Mitglied",
  shopmanager: "Shopmanager",
  administrator: "Administrator",
};

const STATUS_LABELS: Record<UserStatus, string> = {
  aktiv: "Aktiv",
  passiv: "Passiv",
  inaktiv: "Inaktiv",
};

const INITIAL_NEW_USER: CreateUserForm = {
  kleidlesnummer: "",
  email: "",
  name: "",
  vorname: "",
  passwort: "",
  role: "mitglied",
  status: "aktiv",
};

const toEditableUser = (user: UserDto): EditableUser => ({
  ...user,
  passwordDraft: "",
  message: "",
});

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [users, setUsers] = useState<EditableUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState<UserSortMode>("name-asc");
  const [newUser, setNewUser] = useState<CreateUserForm>(INITIAL_NEW_USER);
  const [createMessage, setCreateMessage] = useState("");
  const [createTone, setCreateTone] = useState<"error" | "success" | undefined>();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  useEffect(() => {
    const sync = () => setSession(loadAuthSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  const token = session?.token ?? "";
  const currentUserId = session?.user.id ?? "";
  const isAdmin = session?.user.role === "administrator";

  useEffect(() => {
    if (!session) {
      navigate("/konto", { replace: true });
      return;
    }

    if (!isAdmin) {
      navigate("/", { replace: true });
      return;
    }

    const loadUsers = async () => {
      try {
        const result = await getUsersForAdmin(token);
        setUsers(result.map(toEditableUser));
        setLoadError("");
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : "Benutzer konnten nicht geladen werden.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadUsers();
  }, [isAdmin, navigate, session, token]);

  const visibleUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filteredUsers = users.filter((user) => {
      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        user.vorname,
        user.name,
        user.email,
        user.kleidlesnummer,
        user.role,
        ROLE_LABELS[user.role],
        user.status,
        STATUS_LABELS[user.status],
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });

    return [...filteredUsers].sort((left, right) => {
      if (sortMode === "name-desc") {
        return `${right.vorname} ${right.name}`.localeCompare(`${left.vorname} ${left.name}`, "de-DE");
      }

      if (sortMode === "created-desc") {
        return new Date(right.createdAtUtc).getTime() - new Date(left.createdAtUtc).getTime();
      }

      if (sortMode === "created-asc") {
        return new Date(left.createdAtUtc).getTime() - new Date(right.createdAtUtc).getTime();
      }

      if (sortMode === "role-asc") {
        return ROLE_LABELS[left.role].localeCompare(ROLE_LABELS[right.role], "de-DE");
      }

      if (sortMode === "role-desc") {
        return ROLE_LABELS[right.role].localeCompare(ROLE_LABELS[left.role], "de-DE");
      }

      if (sortMode === "status-asc") {
        return STATUS_LABELS[left.status].localeCompare(STATUS_LABELS[right.status], "de-DE");
      }

      if (sortMode === "status-desc") {
        return STATUS_LABELS[right.status].localeCompare(STATUS_LABELS[left.status], "de-DE");
      }

      return `${left.vorname} ${left.name}`.localeCompare(`${right.vorname} ${right.name}`, "de-DE");
    });
  }, [searchTerm, sortMode, users]);

  const updateLocalUser = (userId: string, updater: (current: EditableUser) => EditableUser) => {
    setUsers((prev) => prev.map((user) => (user.id === userId ? updater(user) : user)));
  };

  const handleFieldChange = (userId: string, field: keyof AdminUpdateUserPayload, value: string) => {
    updateLocalUser(userId, (current) => ({
      ...current,
      [field]: value,
      message: "",
    }));
  };

  const handleCreateFieldChange = (field: keyof CreateUserForm, value: string) => {
    setNewUser((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (createTone === "error") {
      setCreateMessage("");
      setCreateTone(undefined);
    }
  };

  const handleOpenCreateDialog = () => {
    setNewUser(INITIAL_NEW_USER);
    setCreateMessage("");
    setCreateTone(undefined);
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewUser(INITIAL_NEW_USER);

    if (createTone === "error") {
      setCreateMessage("");
      setCreateTone(undefined);
    }
  };

  const handleCreateUser = async () => {
    try {
      const createdUser = await createUserAsAdmin(token, {
        ...newUser,
        kleidlesnummer: newUser.kleidlesnummer.trim(),
        email: newUser.email.trim(),
        name: newUser.name.trim(),
        vorname: newUser.vorname.trim(),
        passwort: newUser.passwort,
      });

      setUsers((prev) => [toEditableUser(createdUser), ...prev]);
      setCreateMessage("Benutzer wurde angelegt.");
      setCreateTone("success");
      setIsCreateDialogOpen(false);
      setNewUser(INITIAL_NEW_USER);
    } catch (error) {
      setCreateMessage(error instanceof Error ? error.message : "Benutzer konnte nicht angelegt werden.");
      setCreateTone("error");
    }
  };

  const handleSave = async (user: EditableUser) => {
    try {
      const updated = await updateUserAsAdmin(token, user.id, {
        kleidlesnummer: user.kleidlesnummer,
        email: user.email,
        name: user.name,
        vorname: user.vorname,
        role: user.role,
        status: user.status,
      });

      updateLocalUser(user.id, (current) => ({
        ...current,
        ...updated,
        passwordDraft: current.passwordDraft,
        message: "Benutzer gespeichert.",
        tone: "success",
      }));
    } catch (error) {
      updateLocalUser(user.id, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Benutzer konnte nicht gespeichert werden.",
        tone: "error",
      }));
    }
  };

  const handlePasswordReset = async (user: EditableUser) => {
    try {
      await resetUserPasswordAsAdmin(token, user.id, {
        neuesPasswort: user.passwordDraft,
      });

      updateLocalUser(user.id, (current) => ({
        ...current,
        passwordDraft: "",
        message: "Passwort wurde aktualisiert.",
        tone: "success",
      }));
    } catch (error) {
      updateLocalUser(user.id, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Passwort konnte nicht aktualisiert werden.",
        tone: "error",
      }));
    }
  };

  const handleDelete = async (user: EditableUser) => {
    if (user.id === currentUserId) {
      updateLocalUser(user.id, (current) => ({
        ...current,
        message: "Das aktuell angemeldete Administrator-Konto kann hier nicht gelöscht werden.",
        tone: "error",
      }));
      return;
    }

    const confirmed = window.confirm(`Soll ${user.vorname} ${user.name} wirklich gelöscht werden?`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteUserAsAdmin(token, user.id);
      setUsers((prev) => prev.filter((entry) => entry.id !== user.id));
    } catch (error) {
      updateLocalUser(user.id, (current) => ({
        ...current,
        message: error instanceof Error ? error.message : "Benutzer konnte nicht gelöscht werden.",
        tone: "error",
      }));
    }
  };

  const isCreateDisabled =
    newUser.kleidlesnummer.trim().length === 0 ||
    newUser.email.trim().length === 0 ||
    newUser.name.trim().length === 0 ||
    newUser.vorname.trim().length === 0 ||
    newUser.passwort.trim().length < 12;

  return (
    <>
      <SinglePage
        title="Mitgliederverwaltung"
        heroBanner={
          <HeroBanner>
            <Eyebrow>Administratorbereich</Eyebrow>
            <HeroTitle>Mitglieder verwalten.</HeroTitle>
            <HeroText>
              Hier können Benutzerkonten gepflegt, neue Mitglieder angelegt, Rollen und Status angepasst,
              Passwörter neu gesetzt und Benutzer gelöscht werden. Dieser Bereich ist ausschließlich für
              Administratoren gedacht.
            </HeroText>
          </HeroBanner>
        }
        content={
          <ContentContainer>
            <NoticeCard>
              <NoticeTitle>Benutzerverwaltung</NoticeTitle>
              <NoticeText>
                {isLoading
                  ? "Benutzer werden geladen."
                  : loadError
                    ? loadError
                    : `${visibleUsers.length} Benutzerkonten sichtbar.`}
              </NoticeText>
            </NoticeCard>

            <NoticeCard>
              <NoticeTitle>Neuen Benutzer anlegen</NoticeTitle>
              <NoticeText>
                Die Benutzeranlage erfolgt in einem separaten Dialog, damit die eigentliche Mitgliederverwaltung
                übersichtlich bleibt.
              </NoticeText>
              {createMessage ? <Message $tone={createTone}>{createMessage}</Message> : null}
              <ActionRow style={{ marginTop: "1rem" }}>
                <Button type="button" $variant="primary" onClick={handleOpenCreateDialog}>
                  Benutzerdialog öffnen
                </Button>
              </ActionRow>
            </NoticeCard>

            <NoticeCard>
              <FormGrid>
                <Field>
                  <FieldLabel>Suche</FieldLabel>
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Vorname, Name, E-Mail, Kleidlesnummer, Rolle oder Status"
                  />
                </Field>
                <Field>
                  <FieldLabel>Sortierung</FieldLabel>
                  <Select value={sortMode} onChange={(event) => setSortMode(event.target.value as UserSortMode)}>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="created-desc">Neueste Konten zuerst</option>
                    <option value="created-asc">Älteste Konten zuerst</option>
                    <option value="role-asc">Rolle A-Z</option>
                    <option value="role-desc">Rolle Z-A</option>
                    <option value="status-asc">Status A-Z</option>
                    <option value="status-desc">Status Z-A</option>
                  </Select>
                </Field>
              </FormGrid>
            </NoticeCard>

            {!isLoading && !loadError
              ? visibleUsers.map((user) => (
                  <UserCard key={user.id}>
                    <UserHeader>
                      <div>
                        <UserTitle>
                          {user.vorname} {user.name}
                        </UserTitle>
                        <UserSubline>
                          {user.email} | Kleidlesnummer {user.kleidlesnummer}
                        </UserSubline>
                      </div>
                      <BadgeRow>
                        <RoleBadge>{ROLE_LABELS[user.role]}</RoleBadge>
                        <StatusBadge>{STATUS_LABELS[user.status]}</StatusBadge>
                      </BadgeRow>
                    </UserHeader>

                    <FormGrid>
                      <Field>
                        <FieldLabel>Vorname</FieldLabel>
                        <Input value={user.vorname} onChange={(event) => handleFieldChange(user.id, "vorname", event.target.value)} />
                      </Field>
                      <Field>
                        <FieldLabel>Name</FieldLabel>
                        <Input value={user.name} onChange={(event) => handleFieldChange(user.id, "name", event.target.value)} />
                      </Field>
                      <Field>
                        <FieldLabel>E-Mail</FieldLabel>
                        <Input value={user.email} onChange={(event) => handleFieldChange(user.id, "email", event.target.value)} />
                      </Field>
                      <Field>
                        <FieldLabel>Kleidlesnummer</FieldLabel>
                        <Input
                          value={user.kleidlesnummer}
                          onChange={(event) => handleFieldChange(user.id, "kleidlesnummer", event.target.value)}
                        />
                      </Field>
                      <Field>
                        <FieldLabel>Rolle</FieldLabel>
                        <Select value={user.role} onChange={(event) => handleFieldChange(user.id, "role", event.target.value)}>
                          <option value="mitglied">Mitglied</option>
                          <option value="shopmanager">Shopmanager</option>
                          <option value="administrator">Administrator</option>
                        </Select>
                      </Field>
                      <Field>
                        <FieldLabel>Status</FieldLabel>
                        <Select value={user.status} onChange={(event) => handleFieldChange(user.id, "status", event.target.value)}>
                          <option value="aktiv">Aktiv</option>
                          <option value="passiv">Passiv</option>
                          <option value="inaktiv">Inaktiv</option>
                        </Select>
                      </Field>
                    </FormGrid>

                    <PasswordRow>
                      <Field>
                        <FieldLabel>Neues Passwort setzen</FieldLabel>
                        <Input
                          type="password"
                          value={user.passwordDraft}
                          placeholder="Mindestens 12 Zeichen"
                          onChange={(event) =>
                            updateLocalUser(user.id, (current) => ({
                              ...current,
                              passwordDraft: event.target.value,
                              message: "",
                            }))
                          }
                        />
                      </Field>
                      <Button
                        type="button"
                        $variant="ghost"
                        onClick={() => void handlePasswordReset(user)}
                        disabled={user.passwordDraft.trim().length === 0}
                      >
                        Passwort setzen
                      </Button>
                    </PasswordRow>

                    {user.message ? <Message $tone={user.tone}>{user.message}</Message> : null}

                    <ActionRow>
                      <Button type="button" $variant="primary" onClick={() => void handleSave(user)}>
                        Aenderungen speichern
                      </Button>
                      <Button type="button" $variant="danger" onClick={() => void handleDelete(user)}>
                        Benutzer löschen
                      </Button>
                    </ActionRow>
                  </UserCard>
                ))
              : null}
          </ContentContainer>
        }
      />

      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "1.6rem",
            color: "#fff",
            border: "1px solid rgba(155, 207, 103, 0.16)",
            background: "linear-gradient(180deg, rgba(20,20,20,0.98), rgba(10,10,10,0.98))",
            boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, fontSize: "1.45rem", fontWeight: 700 }}>Neuen Benutzer anlegen</DialogTitle>
        <DialogContent sx={{ pt: "1rem !important" }}>
          <NoticeText style={{ marginBottom: "1rem" }}>
            Lege ein neues Konto mit Rolle, Status und Startpasswort an. Die Eingabefelder entsprechen der
            bestehenden Mitgliederverwaltung.
          </NoticeText>
          <FormGrid>
            <Field>
              <FieldLabel>Vorname</FieldLabel>
              <Input value={newUser.vorname} onChange={(event) => handleCreateFieldChange("vorname", event.target.value)} />
            </Field>
            <Field>
              <FieldLabel>Name</FieldLabel>
              <Input value={newUser.name} onChange={(event) => handleCreateFieldChange("name", event.target.value)} />
            </Field>
            <Field>
              <FieldLabel>E-Mail</FieldLabel>
              <Input value={newUser.email} onChange={(event) => handleCreateFieldChange("email", event.target.value)} />
            </Field>
            <Field>
              <FieldLabel>Kleidlesnummer</FieldLabel>
              <Input value={newUser.kleidlesnummer} onChange={(event) => handleCreateFieldChange("kleidlesnummer", event.target.value)} />
            </Field>
            <Field>
              <FieldLabel>Rolle</FieldLabel>
              <Select value={newUser.role} onChange={(event) => handleCreateFieldChange("role", event.target.value)}>
                <option value="mitglied">Mitglied</option>
                <option value="shopmanager">Shopmanager</option>
                <option value="administrator">Administrator</option>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Status</FieldLabel>
              <Select value={newUser.status} onChange={(event) => handleCreateFieldChange("status", event.target.value)}>
                <option value="aktiv">Aktiv</option>
                <option value="passiv">Passiv</option>
                <option value="inaktiv">Inaktiv</option>
              </Select>
            </Field>
            <Field style={{ gridColumn: "1 / -1" }}>
              <FieldLabel>Startpasswort</FieldLabel>
              <Input
                type="password"
                value={newUser.passwort}
                placeholder="Mindestens 12 Zeichen"
                onChange={(event) => handleCreateFieldChange("passwort", event.target.value)}
              />
            </Field>
          </FormGrid>
          {createTone === "error" && createMessage ? <Message $tone={createTone}>{createMessage}</Message> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, justifyContent: "flex-end" }}>
          <Button type="button" $variant="ghost" onClick={handleCloseCreateDialog}>
            Abbrechen
          </Button>
          <Button type="button" $variant="primary" onClick={() => void handleCreateUser()} disabled={isCreateDisabled}>
            Benutzer anlegen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminUserManagement;
